import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import { categories } from "@/mocks/categories";
import { galleryImages } from "@/mocks/gallery";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center text-foreground-300">
              <i className="ri-emotion-sad-line text-4xl"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">
              分类未找到
            </h1>
            <p className="text-foreground-600 mb-6">
              抱歉，您查找的分类不存在或已被移除。
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 text-sm bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer font-medium"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryImages = galleryImages.filter(
    (img) => img.category === category.name
  );

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${category.name} - AI美女生成平台`,
            description: category.description,
            url: `https://ai-beauty-platform.com/category/${category.slug}`,
            isPartOf: {
              "@type": "WebSite",
              name: "AI美女生成平台",
              url: "https://ai-beauty-platform.com",
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "首页",
                  item: "https://ai-beauty-platform.com/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: category.name,
                },
              ],
            },
          }),
        }}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-sm text-foreground-500">
              <Link
                to="/"
                className="hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                首页
              </Link>
              <span>/</span>
              <span className="text-foreground-950 font-medium">
                {category.name}
              </span>
            </nav>
          </div>
        </div>

        {/* Category Header */}
        <section className="relative h-[360px] md:h-[420px] overflow-hidden">
          <img
            src={category.imageUrl}
            alt={category.name}
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/60"></div>
          <div className="relative z-10 w-full h-full flex items-center justify-center px-4 md:px-6">
            <div className="text-center max-w-2xl">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm text-white">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
                {category.name}
              </h1>
              <p className="text-white/80 max-w-lg mx-auto leading-relaxed">
                {category.description}
              </p>
              <p className="text-white/50 text-sm mt-4">
                共 {category.count} 个作品
              </p>
            </div>
          </div>
        </section>

        {/* Gallery */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {categoryImages.map((image) => (
                <div
                  key={image.id}
                  className="group relative rounded-xl overflow-hidden bg-background-200 cursor-pointer"
                  data-product-shop
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={image.imageUrl}
                      alt={image.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                    <div>
                      <h3 className="text-sm font-medium text-white mb-1">
                        {image.title}
                      </h3>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {image.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs px-2 py-0.5 rounded-full bg-white/20 text-white/90"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-16">
              <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-6 text-center">
                探索其他分类
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {categories
                  .filter((c) => c.slug !== slug)
                  .map((c) => (
                    <Link
                      key={c.slug}
                      to={`/category/${c.slug}`}
                      className="group p-5 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100/70 text-primary-600 group-hover:bg-primary-500 group-hover:text-white transition-colors duration-300">
                          <i className={`${c.icon} text-lg`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-950">
                            {c.name}
                          </p>
                          <p className="text-xs text-foreground-500">
                            {c.count} 个作品
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}